import { Box, Divider, InputLabel, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";

import { QueryInput } from "components/common/QueryInput";
import { DEFAULT_MAX_COUNT } from "config/constants";
import { ConfidenceSlider } from "features/confidenceSlider/ConfidenceSlider";
import { NewDetectorForm } from "hooks/CreateDetector/useCreateDetectorForm";
import { Typography } from "new-components/typography/Typography";

import { DetectorCreationSettings } from "../steps/Step2";

export interface CreateCountProps {
  countingObjectValue: string;
  maxCapCount: number;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  detectorCreationSettings: DetectorCreationSettings;
  newDetectorForm: NewDetectorForm;
  handleConfidenceThresholdChange: (newValue: number) => void;
  setHasErrors?: (hasErrors: boolean) => void;
}

const placeholders = [
  "Car",
  "Person",
  "Aluminum block",
  "Bottle",
  "Cat",
  "Cardboard box",
  "Hard hat",
  "Coffee cup",
  "Pokemon",
];

const MAX_QUERY_CLASS_NAME_LENGTH = 50;

export function CreateCount(props: CreateCountProps) {
  const [queryError, setQueryError] = useState("");
  const [fadeClass, setFadeClass] = useState("fade-in");
  const [maxCountValueError, setMaxCountValueError] = useState("");
  const [currentPlaceholder, setCurrentPlaceholder] = useState(placeholders[0]);
  const {
    countingObjectValue,
    maxCapCount,
    onInputChange,
    detectorCreationSettings,
    newDetectorForm,
    handleConfidenceThresholdChange,
    setHasErrors,
  } = props;
  const { confidenceThreshold } = newDetectorForm;
  const { MAX_COUNT_LIMIT } = detectorCreationSettings;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value, type, name } = event.target;
    onInputChange(event);
    if (name === "validBboxLabels" && value.length > MAX_QUERY_CLASS_NAME_LENGTH) {
      setQueryError(`Query class name should be less than or equal to ${MAX_QUERY_CLASS_NAME_LENGTH} characters.`);
      return;
    }
    setQueryError("");
    if (type === "number") {
      const parsedNumber = parseInt(value, 10);
      if (parsedNumber <= 0) {
        setMaxCountValueError(`Please enter a value greater than 0.`);
        return;
      }
      const maxLimit = MAX_COUNT_LIMIT || DEFAULT_MAX_COUNT;
      if (parsedNumber > maxLimit) {
        setMaxCountValueError(`You must enter a number less than or equal to ${maxLimit}.`);
        return;
      }
      setMaxCountValueError("");
    }
  };

  useEffect(() => {
    if (setHasErrors) setHasErrors(queryError !== "" || maxCountValueError !== "");
  }, [maxCountValueError, queryError, setHasErrors]);

  useEffect(() => {
    let index = 0;
    let timeoutId: NodeJS.Timeout | null = null;
    const interval = setInterval(() => {
      setFadeClass("fade-out");
      timeoutId = setTimeout(() => {
        setCurrentPlaceholder(placeholders[index]);
        setFadeClass("fade-in");
      }, 300);

      index += 1;
      if (index >= placeholders.length) {
        index = 0;
      }
    }, 1500);

    return () => {
      clearInterval(interval);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <>
      <Box className="flex flex-col gap-2.5 w-full p-[15px] pl-0">
        <InputLabel className="text-sm md:text-base mb-2 text-black">QUERY:</InputLabel>
        <Box display="flex" width="100%">
          <Typography variant="main" fontWeight="bold" className="text-black mr-1.5">
            {"Label each "}
          </Typography>
          <Box flexGrow={0}>
            <QueryInput
              name="validBboxLabels"
              placeholder={currentPlaceholder}
              value={countingObjectValue}
              onChange={handleInputChange}
              className={fadeClass}
              error={queryError}
            />
          </Box>
          <Typography variant="main" fontWeight="bold" className="ml-1 text-black mr-1.5">
            {" in the image. "}
          </Typography>
        </Box>
      </Box>
      <ConfidenceSlider
        value={confidenceThreshold}
        setValue={(value: number) => handleConfidenceThresholdChange(value)}
        setValueCommitted={(value: number) => handleConfidenceThresholdChange(value)}
        defaultShowTradeoff
        hideTradeoffCharts
        showBottomBorder={false}
        totalClasses={Number(maxCapCount) + 2}
      />
      <Typography fontWeight="bold" textTransform="uppercase" className="mb-2">
        Answer Options:
      </Typography>
      <Divider />
      <Box className="flex flex-col min-w-full">
        <InputLabel className="text-sm md:text-base mb-2 text-black">Maximum count per image:</InputLabel>
        <TextField
          data-testid="maxcount-input"
          className="text-sm md:text-base text-neutral-700 font-bold my-2 uppercase bg-white"
          type="number"
          name="maxCapCount"
          size="small"
          value={maxCapCount}
          onWheel={(e) => e.currentTarget.blur()}
          helperText={maxCountValueError}
          onChange={handleInputChange}
          error={!!maxCountValueError}
          FormHelperTextProps={{
            sx: { textTransform: "initial" },
          }}
        />
      </Box>
    </>
  );
}
